import { useState, useCallback } from 'react';
import { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

export interface UsePaginationOptions {
  initialPageSize?: number;
  initialPage?: number;
  initialSortField?: string;
  initialSortOrder?: 'asc' | 'desc';
}

export interface UsePaginationReturn {
  pageIndex: number;
  pageSize: number;
  paginationModel: GridPaginationModel;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  filters: Array<{ field: string; value: any }>;
  quickFilter: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  handleFilterModelChange: (model: GridFilterModel) => void;
  handlePagination: (paginationObj: GridPaginationModel) => void;
  handleSortModelChange: (model: GridSortModel) => void;
}

export function usePagination({
  initialPageSize = 20,
  initialPage = 0,
  initialSortField = 'status',
  initialSortOrder = 'asc',
}: UsePaginationOptions = {}): UsePaginationReturn {
  const [pageIndex, setPageIndex] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [],
  });
  const [filters, setFilters] = useState<{ field: string; value: any }[]>([]);
  const [quickFilter, setQuickFilter] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>(() =>
    initialSortField
      ? [{ field: initialSortField, sort: initialSortOrder }]
      : [],
  );
  const [sortField, setSortField] = useState(initialSortField);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [paginationModel, setPaginationModel] = useState({
    page: pageIndex,
    pageSize: pageSize,
  });

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    setFilters(
      model.items.map(item => ({
        field: item.field,
        value: item.value,
      })),
    );
    setQuickFilter(model.quickFilterValues?.[0] || '');
  }, []);

  const handlePagination = useCallback((paginationObj: GridPaginationModel) => {
    const { page, pageSize: size } = paginationObj;
    setPageIndex(page);
    setPageSize(size);
    setPaginationModel(paginationObj);
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
    if (model.length > 0) {
      setSortField(model[0].field);
      setSortOrder(model[0].sort || 'asc');
    } else {
      // Clear sort when model is empty
      setSortField('');
      setSortOrder('asc');
    }
  }, []);

  return {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    filters,
    quickFilter,
    sortField,
    sortOrder,
    handleFilterModelChange,
    handlePagination,
    handleSortModelChange,
  };
}
